import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import SeoHead from "../components/SeoHead";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Check, Zap, Instagram, Star, Clock, Repeat, Play, ExternalLink, X, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import HrefLangTags from "../components/HrefLangTags";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PostunHazirLogo from "../components/PostunHazirLogo";
import Reveal from "../components/Reveal";
import { useLanguage } from "../contexts/LanguageContext";
import { useCurrency } from "../contexts/CurrencyContext";
import { useUTM } from "../hooks/useUTM";
import { fireQuickPricingLead } from "../lib/pricingCta";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];

// Pricing in base TRY — converted on render via useCurrency().format()
// Default prices (fallback when API not loaded yet)
const DEFAULT_PRICES_TRY = {
  starter: 6500,
  growth:  12500,
  scale:   24500,
};

// Local video reels with ACTUAL VIDEO FRAME posters (extracted with ffmpeg)
const REEL_LABELS = {
  tr: [
    { label: "Kreatif Çalışma", desc: "Marka hikayesi & ürün lansmanı" },
    { label: "Sosyal Medya", desc: "Organik büyüme içerikleri" },
    { label: "İçerik Üretimi", desc: "Postun Hazır - Günlük üretim" },
  ],
  en: [
    { label: "Creative Work", desc: "Brand story & product launch" },
    { label: "Social Media", desc: "Organic growth content" },
    { label: "Content Production", desc: "Postun Hazır - Daily production" },
  ],
  es: [
    { label: "Trabajo Creativo", desc: "Historia de marca y lanzamiento" },
    { label: "Redes Sociales", desc: "Contenido de crecimiento orgánico" },
    { label: "Producción de Contenido", desc: "Postun Hazır - Producción diaria" },
  ],
  de: [
    { label: "Kreativarbeit", desc: "Markengeschichte & Produktlaunch" },
    { label: "Social Media", desc: "Organische Wachstumsinhalte" },
    { label: "Content-Produktion", desc: "Postun Hazır - Tägliche Produktion" },
  ],
  fr: [
    { label: "Travail Créatif", desc: "Histoire de marque & lancement" },
    { label: "Réseaux Sociaux", desc: "Contenu de croissance organique" },
    { label: "Production de Contenu", desc: "Postun Hazır - Production quotidienne" },
  ],
  ar: [
    { label: "عمل إبداعي", desc: "قصة العلامة وإطلاق المنتج" },
    { label: "وسائل التواصل", desc: "محتوى النمو العضوي" },
    { label: "إنتاج المحتوى", desc: "Postun Hazır - إنتاج يومي" },
  ],
};

// Video base URL — /api/media/ in production (Emergent routes /api/* to backend)
const MEDIA_BASE = BACKEND_URL ? BACKEND_URL.replace(/\/api$/, "") + "/api/media" : "/media";

const REEL_VIDEOS = [
  { src: `${MEDIA_BASE}/reel-1.mp4`, poster: `${MEDIA_BASE}/reel-1-poster.jpg`, label: "Kreatif Çalışma", desc: "Marka hikayesi & ürün lansmanı" },
  { src: `${MEDIA_BASE}/reel-2.mp4`, poster: `${MEDIA_BASE}/reel-2-poster.jpg`, label: "Sosyal Medya", desc: "Organik büyüme içerikleri" },
  { src: `${MEDIA_BASE}/reel-3.mp4`, poster: `${MEDIA_BASE}/reel-3-poster.jpg`, label: "İçerik Üretimi", desc: "Postun Hazır - Günlük üretim" },
];

function PricingTier({ tier, popular, t, formatPrice, onChoose, livePrice, liveFeatures }) {
  return (
    <Reveal as="div" className="h-full">
      <div
        data-testid={`postun-pricing-${tier}`}
        className={`relative h-full rounded-3xl p-7 sm:p-8 flex flex-col
                    ${popular
                      ? "bg-postun-blue text-cream border-2 border-postun-yellow shadow-[0_24px_70px_-30px_rgba(252,211,77,0.6)]"
                      : "bg-cream text-postun-blue-deep border-2 border-postun-blue/15"}`}
      >
        {popular ? (
          <span className="absolute -top-3 start-7 inline-flex items-center gap-1 rounded-full bg-postun-yellow text-postun-blue-deep text-[0.65rem] uppercase tracking-[0.18em] font-bold px-3 py-1">
            <Star className="h-3 w-3" /> {t("postun_hazir.pricing.popular")}
          </span>
        ) : null}
        <span className={`text-micro uppercase ${popular ? "text-postun-yellow" : "text-postun-blue/70"}`}>
          {t(`postun_hazir.pricing.${tier}.eyebrow`)}
        </span>
        <h3 className="mt-3 font-display text-h1 font-bold leading-none">
          {t(`postun_hazir.pricing.${tier}.name`)}
        </h3>
        <div className="mt-6 flex items-baseline gap-1">
          <span className="font-display text-display-2 font-extrabold leading-none">
            {formatPrice(livePrice || DEFAULT_PRICES_TRY[tier])}
          </span>
          <span className="text-sm opacity-75">{t("postun_hazir.pricing.month_short")}</span>
        </div>
        <p className={`mt-3 text-sm ${popular ? "text-cream/80" : "text-postun-blue-deep/70"}`}>
          {t(`postun_hazir.pricing.${tier}.tag`)}
        </p>

        <ul className="mt-7 space-y-3 text-sm flex-1">
          {/* Use live features from admin if available, otherwise i18n bullets */}
          {liveFeatures && liveFeatures.length > 0
            ? liveFeatures.map((feat, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <span className={`mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full
                  ${popular ? "bg-postun-yellow text-postun-blue-deep" : "bg-postun-blue text-cream"}`}>
                  <Check className="h-3 w-3" strokeWidth={3} />
                </span>
                <span className={popular ? "text-cream/95" : "text-postun-blue-deep/85"}>{feat}</span>
              </li>
            ))
            : [1, 2, 3, 4].map((i) => (
              <li key={i} className="flex items-start gap-2.5">
                <span className={`mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full
                  ${popular ? "bg-postun-yellow text-postun-blue-deep" : "bg-postun-blue text-cream"}`}>
                  <Check className="h-3 w-3" strokeWidth={3} />
                </span>
                <span className={popular ? "text-cream/95" : "text-postun-blue-deep/85"}>
                  {t(`postun_hazir.pricing.${tier}.bullets.${i}`)}
                </span>
              </li>
            ))
          }
        </ul>

        <button
          type="button"
          onClick={() => onChoose(tier)}
          className={`mt-8 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition-[transform,background-color,color] duration-300
                     ${popular
                       ? "bg-postun-yellow text-postun-blue-deep hover:scale-[1.02]"
                       : "border-2 border-postun-blue text-postun-blue-deep hover:bg-postun-blue hover:text-cream"}`}
          data-testid={`postun-pricing-cta-${tier}`}
        >
          <Zap className="h-4 w-4" />
          {t("postun_hazir.pricing.cta")}
        </button>
      </div>
    </Reveal>
  );
}

// Local video player with blur poster + play button
function ReelEmbed({ reel }) {
  const [playing, setPlaying] = useState(false);
  const videoRef = React.useRef(null);

  const handlePlay = () => {
    setPlaying(true);
    if (videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  };

  const handleVideoClick = () => {
    if (!playing) return;
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play();
      } else {
        videoRef.current.pause();
      }
    }
  };

  return (
    <div className="relative rounded-2xl overflow-hidden border border-postun-blue/30 bg-postun-blue-deep/20 aspect-[9/16] max-w-xs mx-auto shadow-[0_24px_60px_-20px_rgba(37,99,235,0.5)]">
      {/* Blur poster overlay */}
      {!playing && (
        <div className="absolute inset-0 z-10">
          <img
            src={reel.poster}
            alt={reel.label}
            loading="lazy"
            className="h-full w-full object-cover"
            data-testid="reel-poster"
          />
          {/* Soft gradient so play button + labels stay legible over the real video frame */}
          <div className="absolute inset-0 bg-gradient-to-t from-postun-blue-deep/85 via-postun-blue-deep/25 to-postun-blue-deep/10" />
          {/* Play button */}
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
            <button
              type="button"
              onClick={handlePlay}
              className="group relative flex items-center justify-center"
            >
              {/* Pulse rings */}
              <span className="absolute h-20 w-20 rounded-full bg-white/20 animate-ping" />
              <span className="absolute h-16 w-16 rounded-full bg-white/30 animate-ping [animation-delay:200ms]" />
              {/* Main button */}
              <span className="relative flex h-16 w-16 items-center justify-center rounded-full bg-postun-yellow text-postun-blue-deep shadow-[0_8px_24px_rgba(252,211,77,0.6)] transition-transform group-hover:scale-110">
                <Play className="h-7 w-7 ml-1" />
              </span>
            </button>
            <div className="text-center px-4">
              <div className="text-white font-bold text-sm">{reel.label}</div>
              <div className="text-cream/60 text-xs mt-1">{reel.desc}</div>
            </div>
          </div>
        </div>
      )}

      {/* Actual video element */}
      <video
        ref={videoRef}
        src={reel.src}
        poster={reel.poster}
        className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ${playing ? "opacity-100" : "opacity-0"}`}
        controls={playing}
        loop
        playsInline
        onClick={handleVideoClick}
        onEnded={() => setPlaying(false)}
      />
    </div>
  );
}

// Feed lightbox with swipe + prev/next arrows
function FeedLightbox({ src, current, total, onClose, onPrev, onNext }) {
  const touchStartX = React.useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose, onPrev, onNext]);

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-postun-blue-deep/95 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => {
        if (touchStartX.current === null) return;
        const dx = e.changedTouches[0].clientX - touchStartX.current;
        if (Math.abs(dx) > 50) { dx < 0 ? onNext() : onPrev(); }
        touchStartX.current = null;
      }}
    >
      {/* Counter */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 text-cream/60 text-sm z-10">{current} / {total}</div>
      {/* Close */}
      <button type="button" onClick={onClose}
        className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center text-cream z-10">
        <X className="h-5 w-5" />
      </button>
      {/* Prev */}
      <button type="button" onClick={(e) => { e.stopPropagation(); onPrev(); }}
        className="absolute left-4 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center text-cream z-10">
        <ChevronLeft className="h-6 w-6" />
      </button>
      {/* Next */}
      <button type="button" onClick={(e) => { e.stopPropagation(); onNext(); }}
        className="absolute right-4 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center text-cream z-10">
        <ChevronRight className="h-6 w-6" />
      </button>
      <motion.div
        key={src}
        initial={{ scale: 0.88, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.88, opacity: 0 }}
        transition={{ type: "spring", stiffness: 280, damping: 24 }}
        onClick={(e) => e.stopPropagation()} className="relative max-w-xl w-full"
      >
        <img src={src} alt="Postun Hazır" className="w-full max-h-[82vh] object-contain rounded-2xl shadow-2xl" />
        <a href="https://instagram.com/postunhazir" target="_blank" rel="noopener noreferrer"
          className="mt-3 flex items-center justify-center gap-2 text-cream/70 hover:text-cream text-sm transition-colors">
          <Instagram className="h-4 w-4" /> @postunhazir <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </motion.div>
    </motion.div>
  );
}

export default function PostunHazirPage() {
  const { t, lang } = useLanguage();
  const { format: formatPrice } = useCurrency();
  const { utm, referrer } = useUTM();
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [pricingData, setPricingData] = useState(null); // live pricing from API

  // 9 feed images from projeler
  const FEED_IMAGES = [
    "/projeler/postunhazir_1776700579_3879568086261997846_40209662941.jpg",
    "/projeler/postunhazir_1746276095_3624349039141178197_40209662941.jpg",
    "/projeler/postunhazir_1746276095_3624349039141175670_40209662941.jpg",
    "/projeler/postunhazir_1776700579_3879568086815659792_40209662941.jpg",
    "/projeler/postunhazir_1733155802_3514288033514426975_40209662941.jpg",
    "/projeler/postunhazir_1746275810_3624346644940226024_40209662941.jpg",
    "/projeler/postunhazir_1776700579_3879568086589130594_40209662941.jpg",
    "/projeler/postunhazir_1776791747_3880332871433880364_40209662941.jpg",
    "/projeler/postunhazir_1776792574_3880339814173016193_40209662941.jpg",
  ];

  const openLightbox = (idx) => setLightboxIdx(idx);
  const closeLightbox = () => setLightboxIdx(null);
  const prevImage = () => setLightboxIdx((i) => (i > 0 ? i - 1 : FEED_IMAGES.length - 1));
  const nextImage = () => setLightboxIdx((i) => (i < FEED_IMAGES.length - 1 ? i + 1 : 0));

  // Fetch live pricing from admin-editable service
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/services/postun-hazir`, { params: { lang } })
      .then((r) => { if (!cancelled) setPricingData(r.data?.pricing || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [lang]);

  // Get price for a tier from live data or fallback
  const getTierPrice = (tierId) => {
    if (pricingData) {
      const t = pricingData.find((p) => p.id === tierId);
      if (t?.price_try) return t.price_try;
    }
    return DEFAULT_PRICES_TRY[tierId] || 0;
  };

  // Get features for a tier from live data
  const getTierFeatures = (tierId) => {
    if (pricingData) {
      const t = pricingData.find((p) => p.id === tierId);
      if (t) {
        if (t[`features_${lang}`]?.length) return t[`features_${lang}`];
        if (t.features?.length) return t.features;
      }
    }
    return null; // use i18n bullets
  };

  const handleChoosePricingTier = (tier) => {
    const tierLabel = t(`postun_hazir.pricing.${tier}.name`);
    const tierPrice = formatPrice(getTierPrice(tier));
    toast.message(t("form.success") || "WhatsApp'a yönlendiriliyorsunuz…");
    fireQuickPricingLead({
      serviceLabel: "Postun Hazır",
      serviceSlug: "postun-hazir",
      tierLabel,
      formattedPrice: tierPrice,
      lang,
      utm,
      referrer,
    });
  };

  const pageTitle = `Postun Hazır · Yastık Medya`;
  const pageDesc = t("postun_hazir.hero_subtitle");

  return (
    <div data-testid="postun-hazir-page" className="min-h-screen bg-cream text-ink">
      <SeoHead title={pageTitle} description={pageDesc} path={`/${lang}/postun-hazir`} imageKey="postun-hazir" lang={lang} themeColor="#2563EB" />
      <HrefLangTags pathSuffix="postun-hazir" />
      <Header />

      <main>
        {/* Hero — full blue */}
        <section
          data-testid="postun-hazir-hero"
          className="relative overflow-hidden bg-postun-blue-deep text-cream py-20 sm:py-section-lg"
        >
          <div className="pointer-events-none absolute -top-32 end-[-12%] h-[520px] w-[520px] rounded-full bg-postun-cyan/35 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-32 start-[-8%] h-[460px] w-[460px] rounded-full bg-postun-blue-glow/45 blur-3xl" />

          <div className="container-page relative grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <PostunHazirLogo asLink={false} tone="cream" size={32} />
              <motion.h1
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: EASE, delay: 0.15 }}
                className="mt-7 font-display font-bold text-display-1 leading-[0.98]"
                data-testid="postun-hazir-hero-title"
              >
                {t("postun_hazir.hero_title_pre")}{" "}
                <span
                  className="bg-clip-text text-transparent"
                  style={{
                    backgroundImage:
                      "linear-gradient(120deg, #FCD34D 0%, #7CEDD9 50%, #FFFFFF 100%)",
                  }}
                >
                  {t("postun_hazir.hero_title_accent")}
                </span>
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: EASE, delay: 0.4 }}
                className="mt-7 max-w-2xl text-body-lg text-cream/85 leading-relaxed"
              >
                {t("postun_hazir.hero_subtitle")}
              </motion.p>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.7 }}
                className="mt-10 flex flex-wrap items-center gap-4"
              >
                <Link
                  to={`/${lang}/contact?service=creative`}
                  className="inline-flex items-center gap-2 rounded-full bg-postun-yellow text-postun-blue-deep px-7 py-3.5 text-sm font-semibold hover:scale-[1.02] transition-transform"
                  data-testid="postun-hero-cta-primary"
                >
                  <Zap className="h-4 w-4" />
                  {t("postun_hazir.hero_cta_primary")}
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <a
                  href="https://instagram.com/postunhazir"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full border-2 border-cream/30 bg-transparent text-cream px-7 py-3.5 text-sm font-semibold hover:border-cream hover:bg-cream hover:text-postun-blue-deep transition-colors"
                >
                  <Instagram className="h-4 w-4" />
                  @postunhazir
                </a>
              </motion.div>
            </div>

            {/* Stat cards on the right */}
            <div className="lg:col-span-5 grid grid-cols-2 gap-3 sm:gap-4">
              {[
                { k: "stat_speed", icon: Clock },
                { k: "stat_brand", icon: Star },
                { k: "stat_volume", icon: Repeat },
                { k: "stat_clients", icon: Check },
              ].map((s) => (
                <div
                  key={s.k}
                  className="rounded-2xl bg-postun-blue/45 backdrop-blur-md border border-cream/15 p-5 sm:p-6"
                >
                  <s.icon className="h-5 w-5 text-postun-yellow" />
                  <div className="mt-3 font-display text-h1 font-bold leading-none">
                    {t(`postun_hazir.${s.k}.big`)}
                  </div>
                  <p className="mt-2 text-xs uppercase tracking-[0.18em] text-cream/70">
                    {t(`postun_hazir.${s.k}.small`)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How it works — light section */}
        <section className="bg-cream py-section sm:py-section-lg" data-testid="postun-hazir-how">
          <div className="container-page">
            <Reveal as="div" className="max-w-2xl">
              <span className="eyebrow">
                <span className="h-1 w-1 rounded-full bg-postun-blue" />
                {t("postun_hazir.how_eyebrow")}
              </span>
              <h2 className="mt-5 font-display text-display-2 font-semibold text-postun-blue-deep">
                {t("postun_hazir.how_title_pre")}{" "}
                <span
                  className="bg-clip-text text-transparent"
                  style={{
                    backgroundImage:
                      "linear-gradient(120deg, #2563EB 0%, #06B6D4 60%, #FCD34D 100%)",
                  }}
                >
                  {t("postun_hazir.how_title_accent")}
                </span>
              </h2>
            </Reveal>

            <div className="mt-14 grid gap-8 md:grid-cols-2 lg:grid-cols-4">
              {["brief", "draft", "review", "deliver"].map((s, i) => (
                <Reveal as="div" key={s} delay={i * 0.08}>
                  <div className="font-display text-display-2 font-bold leading-none text-postun-blue/30">
                    0{i + 1}
                  </div>
                  <h3 className="mt-4 font-display text-h2 font-bold text-postun-blue-deep">
                    {t(`postun_hazir.steps.${s}.title`)}
                  </h3>
                  <p className="mt-2.5 text-sm text-ink/70 leading-relaxed">
                    {t(`postun_hazir.steps.${s}.desc`)}
                  </p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* Instagram preview feed */}
        <section className="bg-cream py-section border-t border-mist" data-testid="postun-hazir-feed">
          <div className="container-page">
            <Reveal as="div" className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <span className="eyebrow">
                  <Instagram className="h-3 w-3 text-postun-blue" />
                  {t("postun_hazir.feed_eyebrow")}
                </span>
                <h2 className="mt-4 font-display text-h1 font-bold text-postun-blue-deep">
                  {t("postun_hazir.feed_title")}
                </h2>
              </div>
              <a
                href="https://instagram.com/postunhazir"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-postun-blue text-cream px-5 py-2.5 text-sm font-semibold hover:bg-postun-blue-deep transition-colors"
                data-testid="postun-ig-all-btn"
              >
                <Instagram className="h-4 w-4" />
                {t("postun_hazir.feed_ig_button", "See All Our Work on Instagram")}
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </Reveal>

            <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-3 sm:gap-4">
              {/* 9 feed images — clickable for lightbox preview */}
              {FEED_IMAGES.map((src, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => openLightbox(idx)}
                  className="group relative block overflow-hidden rounded-2xl bg-mist aspect-square cursor-zoom-in"
                  data-testid={`postun-feed-${idx}`}
                >
                  <img
                    src={src}
                    alt={`Postun Hazır çalışma ${idx + 1}`}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-postun-blue-deep/0 group-hover:bg-postun-blue-deep/55 transition-colors duration-300 flex items-center justify-center">
                    <Instagram className="h-6 w-6 text-cream opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </button>
              ))}
            </div>

            {/* CTA to see more on Instagram */}
            <div className="mt-8 text-center">
              <a
                href="https://instagram.com/postunhazir"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-postun-blue-deep border-b-2 border-postun-blue hover:opacity-75 transition-opacity"
              >
                <Instagram className="h-4 w-4" />
                {t("postun_hazir.feed_follow", "Follow @postunhazir for more")} →
              </a>
            </div>
          </div>
        </section>

        {/* Featured Reels Section */}
        <section className="bg-postun-blue py-section border-t border-postun-blue/30" data-testid="postun-hazir-reels">
          <div className="container-page">
            <Reveal as="div" className="text-center mb-12">
              <span className="eyebrow text-cream/60">
                <Play className="h-3 w-3 text-postun-yellow" />
                {t("postun_hazir.reels_eyebrow", "Featured Reels")}
              </span>
              <h2 className="mt-5 font-display text-display-2 font-bold text-cream">
                {t("postun_hazir.reels_title_pre", "Watch Our")}{" "}
                <span className="text-postun-yellow">{t("postun_hazir.reels_title_accent", "Creative Power")}</span>
              </h2>
              <p className="mt-4 text-cream/70 max-w-xl mx-auto">
                {t("postun_hazir.reels_subtitle", "A glimpse of the content we produce for our clients. Every post is customized to your brand tone.")}
              </p>
            </Reveal>

            <div className="grid gap-6 sm:grid-cols-3">
              {REEL_VIDEOS.map((reel, i) => {
                const reelLabels = (REEL_LABELS[lang] || REEL_LABELS.en)[i];
                const reelWithLabels = { ...reel, label: reelLabels.label, desc: reelLabels.desc };
                return (
                <Reveal key={reel.src} as="div" delay={i * 0.12}>
                  <div className="text-center">
                    <ReelEmbed reel={reelWithLabels} />
                    <div className="mt-3 flex items-center justify-center gap-2">
                      <span className="text-sm font-medium text-cream/80">{reelLabels.label}</span>
                    </div>
                    <p className="text-xs text-cream/50 mt-1">{reelLabels.desc}</p>
                  </div>
                </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        {/* Pricing tiers */}
        <section className="bg-postun-blue-deep py-section sm:py-section-lg" data-testid="postun-hazir-pricing">
          <div className="container-page">
            <Reveal as="div" className="max-w-2xl">
              <span className="eyebrow text-cream/60">
                <span className="h-1 w-1 rounded-full bg-postun-yellow" />
                {t("postun_hazir.pricing.eyebrow")}
              </span>
              <h2 className="mt-5 font-display text-display-2 font-bold text-cream">
                {t("postun_hazir.pricing.title_pre")}{" "}
                <span className="text-postun-yellow">
                  {t("postun_hazir.pricing.title_accent")}
                </span>
              </h2>
              <p className="mt-5 text-body-lg text-cream/75 max-w-xl">
                {t("postun_hazir.pricing.subtitle")}
              </p>
            </Reveal>

            <div className="mt-12 grid gap-6 md:grid-cols-3">
              <PricingTier tier="starter" popular={false} t={t} formatPrice={formatPrice} onChoose={handleChoosePricingTier} livePrice={getTierPrice("starter")} liveFeatures={getTierFeatures("starter")} />
              <PricingTier tier="growth" popular={true} t={t} formatPrice={formatPrice} onChoose={handleChoosePricingTier} livePrice={getTierPrice("growth")} liveFeatures={getTierFeatures("growth")} />
              <PricingTier tier="scale" popular={false} t={t} formatPrice={formatPrice} onChoose={handleChoosePricingTier} livePrice={getTierPrice("scale")} liveFeatures={getTierFeatures("scale")} />
            </div>

            <p className="mt-8 text-center text-xs text-cream/55">
              {t("postun_hazir.pricing.note")}
            </p>
          </div>
        </section>

        {/* Final CTA back to YM contact */}
        <section
          id="contact"
          className="bg-postun-blue py-section text-cream relative overflow-hidden"
        >
          <div className="pointer-events-none absolute inset-0 bg-grain-dark opacity-50" />
          <div className="container-page relative text-center">
            <Reveal as="div" className="mx-auto max-w-2xl">
              <h2 className="font-display text-display-2 font-bold leading-tight">
                {t("postun_hazir.final_cta_title")}
              </h2>
              <p className="mt-5 text-body-lg text-cream/85">
                {t("postun_hazir.final_cta_subtitle")}
              </p>
              <Link
                to={`/${lang}/contact?service=creative`}
                className="mt-9 inline-flex items-center gap-2 rounded-full bg-postun-yellow text-postun-blue-deep px-9 py-4 text-base font-bold hover:scale-[1.02] transition-transform"
                data-testid="postun-final-cta"
              >
                <Zap className="h-5 w-5" />
                {t("postun_hazir.final_cta_button")}
                <ArrowRight className="h-5 w-5" />
              </Link>
            </Reveal>
          </div>
        </section>
      </main>
      <Footer />

      {/* Image lightbox with swipe */}
      <AnimatePresence>
        {lightboxIdx !== null && (
          <FeedLightbox
            src={FEED_IMAGES[lightboxIdx]}
            current={lightboxIdx + 1}
            total={FEED_IMAGES.length}
            onClose={closeLightbox}
            onPrev={prevImage}
            onNext={nextImage}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
